import { nextTick, readonly, shallowReactive, shallowRef } from 'vue';

import { UI_LOCALES } from '@/01-models/ui-locale';
import type { Strings, StringKey } from '@/strings/catalogs/en';
import type { UiLocale } from '@/strings/types';
import { resolveStandalonePackageLocale } from '@/features/file-protocol-standalone/logic/package-locale';

export type StringBoundaryModule = Partial<Strings> & Partial<Record<string, Strings[StringKey]>>;

export type StringBoundaryLoaders = Readonly<Record<
  UiLocale,
  () => Promise<StringBoundaryModule>
>>;

type StringBoundaryRegistration = {
  boundaryId: string;
  keys: readonly string[];
  loaders: StringBoundaryLoaders;
};

const registries = {
  en: shallowReactive<StringBoundaryModule>({}),
  ja: shallowReactive<StringBoundaryModule>({}),
  'zh-Hans': shallowReactive<StringBoundaryModule>({}),
  'pt-BR': shallowReactive<StringBoundaryModule>({}),
  es: shallowReactive<StringBoundaryModule>({}),
  ko: shallowReactive<StringBoundaryModule>({}),
  de: shallowReactive<StringBoundaryModule>({}),
} satisfies Record<UiLocale, StringBoundaryModule>;
const boundaryRegistrations = new Map<string, StringBoundaryRegistration>();
const boundaryIdsByKey = new Map<string, Set<string>>();
const loadedBoundaryModules: Record<UiLocale, Map<string, StringBoundaryModule>> = {
  en: new Map(),
  ja: new Map(),
  'zh-Hans': new Map(),
  'pt-BR': new Map(),
  es: new Map(),
  ko: new Map(),
  de: new Map(),
};
type StringBoundaryLoad = {
  promise: Promise<void>;
  cancel: () => void;
  cancellationState: 'active' | 'cancelled';
};
const loadingBoundaries = new Map<string, StringBoundaryLoad>();
const usedBoundaryIds = new Set<string>();
const warmedBoundaryIds = new Set<string>();
const scheduledBoundaryWarmups = new Map<string, () => void>();
const nonMessageAccessorProperties = new Set([
  '_isVue',
  'then',
  'toJSON',
]);
/* eslint-disable local-rules-named-args/require-named-args -- Proxy adapters preserve each message's zero-argument or one-object call signature. */
type LazyStringAccessor = (...args: readonly unknown[]) => string | undefined;
type EnsureStringAccessor = (...args: readonly unknown[]) => Promise<string>;
/* eslint-enable local-rules-named-args/require-named-args */

const lazyStringAccessorCache = new Map<string, LazyStringAccessor>();
const ensureStringAccessorCache = new Map<string, EnsureStringAccessor>();
let localeSwitchRequest = 0;
let boundaryUpdateState: 'idle' | 'updating' = 'idle';
const deferredRegistryKeys = new Set<string>();

function beginBoundaryUpdate(): void {
  // Vite serializes update messages. A rejected accept callback can skip the
  // matching afterUpdate, so the next batch replaces that unfinished state.
  boundaryUpdateState = 'updating';
}

async function finishBoundaryUpdate(): Promise<void> {
  // Vue queues owner replacement during HMR. Keep old rendered strings available
  // until that render finishes, rather than waking the outgoing template early.
  await nextTick();
  boundaryUpdateState = 'idle';
  const keys = [...deferredRegistryKeys];
  deferredRegistryKeys.clear();
  for (const key of keys) {
    for (const locale of UI_LOCALES) {
      rebuildRegistryMessage({ key, locale });
    }
  }
}

if (import.meta.hot) {
  import.meta.hot.on('vite:beforeUpdate', beginBoundaryUpdate);
  import.meta.hot.on('vite:afterUpdate', finishBoundaryUpdate);
}

export function resolveBrowserLocale(): UiLocale {
  const browserLocale = typeof navigator === 'undefined' ? undefined : navigator.language;
  const normalized = browserLocale?.toLowerCase();
  if (normalized === 'ja' || normalized?.startsWith('ja-') === true) {
    return 'ja';
  }
  if (
    normalized === 'zh'
    || normalized === 'zh-hans'
    || normalized?.startsWith('zh-hans-') === true
    || normalized === 'zh-cn'
    || normalized?.startsWith('zh-cn-') === true
    || normalized === 'zh-sg'
    || normalized?.startsWith('zh-sg-') === true
  ) {
    return 'zh-Hans';
  }
  if (normalized === 'pt' || normalized === 'pt-br' || normalized?.startsWith('pt-br-') === true) {
    return 'pt-BR';
  }
  if (normalized === 'es' || normalized?.startsWith('es-') === true) {
    return 'es';
  }
  if (normalized === 'ko' || normalized?.startsWith('ko-') === true) {
    return 'ko';
  }
  if (normalized === 'de' || normalized?.startsWith('de-') === true) {
    return 'de';
  }
  return 'en';
}

function applyDocumentLocale({ locale }: {
  locale: UiLocale;
}): void {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale;
  }
}

function resolveEffectiveLocale({ locale }: {
  locale: UiLocale;
}): UiLocale {
  return resolveStandalonePackageLocale() ?? locale;
}

const initialLocale = resolveEffectiveLocale({ locale: resolveBrowserLocale() });
const currentLocaleState = shallowRef<UiLocale>(initialLocale);
applyDocumentLocale({ locale: initialLocale });
export const currentLocale = readonly(currentLocaleState);

function boundaryLoadKey({ boundaryId, locale }: {
  boundaryId: string;
  locale: UiLocale;
}): string {
  return `${locale}:${boundaryId}`;
}

function installBoundaryModule({ registration, locale, module }: {
  registration: StringBoundaryRegistration;
  locale: UiLocale;
  module: StringBoundaryModule;
}): void {
  const loadedMessages: StringBoundaryModule = {};
  for (const key of registration.keys) {
    const message = module[key];
    if (typeof message !== 'function') {
      throw new Error(
        `Boundary Strings boundary ${registration.boundaryId} did not provide message ${key} for locale ${locale}.`,
      );
    }
    loadedMessages[key] = message;
  }
  loadedBoundaryModules[locale].set(registration.boundaryId, loadedMessages);
  Object.assign(registries[locale], loadedMessages);
}

async function loadBoundaryGeneration({ boundaryId, locale }: {
  boundaryId: string;
  locale: UiLocale;
}): Promise<void> {
  if (loadedBoundaryModules[locale].has(boundaryId)) {
    return;
  }
  const loadKey = boundaryLoadKey({ boundaryId, locale });
  const registration = boundaryRegistrations.get(boundaryId);
  if (registration === undefined) {
    // A locale preparation may already have captured a boundary removed by HMR.
    return;
  }
  const existing = loadingBoundaries.get(loadKey);
  if (existing !== undefined) {
    try {
      await existing.promise;
    } catch (error) {
      if (boundaryRegistrations.get(boundaryId) === registration
        && existing.cancellationState === 'active') {
        throw error;
      }
    }
    return;
  }

  let cancel = () => {};
  const cancelled = new Promise<void>((resolve) => {
    cancel = resolve;
  });
  async function loadModule({ registration }: { registration: StringBoundaryRegistration }): Promise<void> {
    const result = await Promise.race([
      registration.loaders[locale]().then((module) => ({ status: 'loaded' as const, module })),
      cancelled.then(() => ({ status: 'cancelled' as const })),
    ]);
    switch (result.status) {
    case 'cancelled':
      return;
    case 'loaded':
      break;
    default: {
      const exhaustive: never = result;
      throw new Error(`Unexpected Boundary Strings load result: ${String(exhaustive)}`);
    }
    }
    const { module } = result;
    if (boundaryRegistrations.get(boundaryId) !== registration
      || loadingBoundaries.get(loadKey) !== load) {
      return;
    }
    installBoundaryModule({ registration, locale, module });
  }
  const load: StringBoundaryLoad = {
    cancel: () => {
      load.cancellationState = 'cancelled';
      cancel();
    },
    cancellationState: 'active',
    promise: loadModule({ registration }),
  };
  loadingBoundaries.set(loadKey, load);
  try {
    await load.promise;
  } catch (error) {
    if (boundaryRegistrations.get(boundaryId) === registration
      && load.cancellationState === 'active') {
      throw error;
    }
  } finally {
    if (loadingBoundaries.get(loadKey) === load) {
      loadingBoundaries.delete(loadKey);
    }
  }
}

async function ensureBoundaryLoaded({ boundaryId, locale }: {
  boundaryId: string;
  locale: UiLocale;
}): Promise<void> {
  while (boundaryRegistrations.has(boundaryId) && !loadedBoundaryModules[locale].has(boundaryId)) {
    await loadBoundaryGeneration({ boundaryId, locale });
  }
}

function resolveBoundaryId({ key, locale }: {
  key: string;
  locale: UiLocale;
}): string {
  const boundaryIds = boundaryIdsByKey.get(key);
  if (boundaryIds === undefined || boundaryIds.size === 0) {
    throw new Error(`Boundary Strings key ${String(key)} has not been registered by a loaded module.`);
  }

  const candidates = [...boundaryIds].map((boundaryId) => {
    const registration = boundaryRegistrations.get(boundaryId);
    if (registration === undefined) {
      throw new Error(`Missing Boundary Strings registration for ${boundaryId}.`);
    }
    return {
      boundaryId,
      isLoadedOrLoading: loadedBoundaryModules[locale].has(boundaryId)
        || loadingBoundaries.has(boundaryLoadKey({ boundaryId, locale })),
      keyCount: registration.keys.length,
    };
  });
  candidates.sort((left, right) => {
    if (left.isLoadedOrLoading !== right.isLoadedOrLoading) {
      return left.isLoadedOrLoading ? -1 : 1;
    }
    return left.keyCount - right.keyCount || left.boundaryId.localeCompare(right.boundaryId);
  });

  const boundaryId = candidates[0]?.boundaryId;
  if (boundaryId === undefined) {
    throw new Error(`Boundary Strings key ${String(key)} has no usable boundary registration.`);
  }
  return boundaryId;
}

async function ensureKeyLoaded({ key, locale }: {
  key: string;
  locale: UiLocale;
}): Promise<void> {
  while (getActiveMessage({ key, locale }) === undefined) {
    const boundaryId = resolveBoundaryId({ key, locale });
    usedBoundaryIds.add(boundaryId);
    // Replacement may remove this key from the selected owner. After each
    // generation, resolve the key again rather than loading unrelated messages.
    await loadBoundaryGeneration({ boundaryId, locale });
  }
}

function getLoadedMessage({ key, locale }: {
  key: string;
  locale: UiLocale;
}): Strings[StringKey] | undefined {
  return registries[locale][key];
}

function getActiveMessage({ key, locale }: {
  key: string;
  locale: UiLocale;
}): Strings[StringKey] | undefined {
  // During HMR, the render registry can retain an outgoing owner's message.
  // Imperative reads must use a still-registered, loaded owner instead.
  const renderedMessage = getLoadedMessage({ key, locale });
  let firstLoadedMessage: Strings[StringKey] | undefined;
  for (const boundaryId of boundaryIdsByKey.get(key) ?? []) {
    const message = loadedBoundaryModules[locale].get(boundaryId)?.[key];
    if (message === undefined) {
      continue;
    }
    // Preserve the current shared-message choice whenever it is still active.
    if (message === renderedMessage) {
      return message;
    }
    firstLoadedMessage ??= message;
  }
  return firstLoadedMessage;
}

function scheduleBoundaryWarmup({ boundaryId }: {
  boundaryId: string;
}): void {
  if (scheduledBoundaryWarmups.has(boundaryId)) {
    return;
  }

  const registration = boundaryRegistrations.get(boundaryId);
  if (registration === undefined) {
    throw new Error(`Missing Boundary Strings registration for ${boundaryId}.`);
  }

  const run = () => {
    scheduledBoundaryWarmups.delete(boundaryId);
    if (boundaryRegistrations.get(boundaryId) !== registration) {
      return;
    }
    warmedBoundaryIds.add(boundaryId);
    void ensureBoundaryLoaded({
      boundaryId,
      locale: currentLocaleState.value,
    }).catch((error: unknown) => {
      if (boundaryRegistrations.get(boundaryId) !== registration) {
        return;
      }
      warmedBoundaryIds.delete(boundaryId);
      console.error('[Boundary Strings] Failed to warm a message boundary.', error);
    });
  };

  // Registration means the owning JavaScript module is already close to use.
  // Warming during idle time prepares component, composable, and helper text
  // without maintaining a second UI graph or adding preload calls to app code.
  if (typeof globalThis.requestIdleCallback === 'function') {
    const handle = globalThis.requestIdleCallback(run, { timeout: 1000 });
    scheduledBoundaryWarmups.set(boundaryId, () => {
      globalThis.cancelIdleCallback(handle);
    });
    return;
  }

  const handle = globalThis.setTimeout(run, 200);
  scheduledBoundaryWarmups.set(boundaryId, () => {
    globalThis.clearTimeout(handle);
  });
}

function promoteScheduledBoundaryWarmups(): void {
  for (const [boundaryId, cancel] of scheduledBoundaryWarmups) {
    cancel();
    scheduledBoundaryWarmups.delete(boundaryId);
    warmedBoundaryIds.add(boundaryId);
  }
}

function rebuildRegistryMessage({ key, locale }: {
  key: string;
  locale: UiLocale;
}): void {
  switch (boundaryUpdateState) {
  case 'updating':
    deferredRegistryKeys.add(key);
    return;
  case 'idle':
    break;
  default: {
    const _exhaustive: never = boundaryUpdateState;
    throw new Error(`Unexpected Boundary Strings update state: ${_exhaustive}`);
  }
  }
  const boundaryIds = boundaryIdsByKey.get(key);
  if (boundaryIds !== undefined) {
    for (const boundaryId of boundaryIds) {
      const message = loadedBoundaryModules[locale].get(boundaryId)?.[key];
      if (typeof message === 'function') {
        registries[locale][key] = message;
        return;
      }
    }
  }
  delete registries[locale][key];
}

function clearRegistry({ registry }: {
  registry: StringBoundaryModule;
}): void {
  for (const key of Object.keys(registry)) {
    delete registry[key];
  }
}

function getLazyStringAccessor({ key }: {
  key: string;
}): LazyStringAccessor {
  const cached = lazyStringAccessorCache.get(key);
  if (cached !== undefined) {
    return cached;
  }

  // This Proxy adapter must preserve both zero-argument and one-object message signatures.
  // eslint-disable-next-line local-rules-named-args/require-named-args
  const accessor = (...args: readonly unknown[]): string | undefined => {
    const locale = currentLocaleState.value;
    const message = getLoadedMessage({ key, locale });
    if (message === undefined) {
      void ensureKeyLoaded({ key, locale }).catch((error: unknown) => {
        console.error('[Boundary Strings] Failed to load a message boundary.', error);
      });
      return undefined;
    }
    return Reflect.apply(message, undefined, args) as string;
  };
  lazyStringAccessorCache.set(key, accessor);
  return accessor;
}

function removeStringBoundary({ registration }: {
  registration: StringBoundaryRegistration;
}): void {
  const { boundaryId, keys } = registration;
  if (boundaryRegistrations.get(boundaryId) !== registration) {
    return;
  }

  boundaryRegistrations.delete(boundaryId);
  scheduledBoundaryWarmups.get(boundaryId)?.();
  scheduledBoundaryWarmups.delete(boundaryId);
  usedBoundaryIds.delete(boundaryId);
  warmedBoundaryIds.delete(boundaryId);
  for (const key of keys) {
    const boundaryIds = boundaryIdsByKey.get(key);
    boundaryIds?.delete(boundaryId);
    if (boundaryIds?.size === 0) {
      boundaryIdsByKey.delete(key);
    }
  }
  for (const locale of UI_LOCALES) {
    loadedBoundaryModules[locale].delete(boundaryId);
    const loadKey = boundaryLoadKey({ boundaryId, locale });
    loadingBoundaries.get(loadKey)?.cancel();
    loadingBoundaries.delete(loadKey);
    for (const key of keys) {
      rebuildRegistryMessage({ key, locale });
    }
  }
}

export function registerStringBoundary({ boundaryId, keys, loaders }: {
  boundaryId: string;
  keys: readonly string[];
  loaders: StringBoundaryLoaders;
}): {
  unregister: () => void;
  updateLocale: ({ locale, module }: { locale: UiLocale; module: StringBoundaryModule }) => void;
} {
  if (keys.length === 0 || new Set(keys).size !== keys.length) {
    throw new Error(
      `Boundary Strings boundary ${boundaryId} must register a non-empty list of unique message keys.`,
    );
  }

  const wasUsed = usedBoundaryIds.has(boundaryId);
  const wasWarmed = warmedBoundaryIds.has(boundaryId);
  const previous = boundaryRegistrations.get(boundaryId);
  if (previous !== undefined) {
    removeStringBoundary({ registration: previous });
  }

  const registration = { boundaryId, keys, loaders };
  boundaryRegistrations.set(boundaryId, registration);
  for (const key of keys) {
    const boundaryIds = boundaryIdsByKey.get(key) ?? new Set<string>();
    boundaryIds.add(boundaryId);
    boundaryIdsByKey.set(key, boundaryIds);
  }
  if (wasUsed) {
    usedBoundaryIds.add(boundaryId);
  }
  if (wasWarmed) {
    warmedBoundaryIds.add(boundaryId);
  }

  // Naidan already evaluates async child modules while their parent is idle.
  // Scheduling from registration therefore follows real code proximity and
  // reduces empty first renders without handwritten component-specific hints.
  scheduleBoundaryWarmup({ boundaryId });

  // Both callbacks belong to this registration, not to a later version of the owner.
  return {
    unregister: () => removeStringBoundary({ registration }),
    updateLocale: ({ locale, module }) => {
      if (boundaryRegistrations.get(boundaryId) !== registration) {
        return;
      }
      installBoundaryModule({ registration, locale, module });
      const loadKey = boundaryLoadKey({ boundaryId, locale });
      // Release callers awaiting the obsolete import even if it never settles.
      loadingBoundaries.get(loadKey)?.cancel();
      loadingBoundaries.delete(loadKey);
    },
  };
}

/**
 * Reads a message synchronously for a reactive render path.
 *
 * A missing locale boundary starts loading and returns undefined. Callers
 * must therefore use this accessor only where Vue will evaluate the expression
 * again after the reactive locale registry receives the message implementation.
 */
/* eslint-disable local-rules-named-args/require-named-args -- This type adapter preserves the exact zero-argument or one-object message contract. */
type LazyString<Message> = Message extends () => infer Result
  ? () => Awaited<Result> | undefined
  : Message extends (args: infer Args) => infer Result
    ? (args: Args) => Awaited<Result> | undefined
    : never;
/* eslint-enable local-rules-named-args/require-named-args */

export type LazyStrings = {
  readonly [Key in keyof Strings]: LazyString<Strings[Key]>;
};

export const lazyStrings = new Proxy({}, {
  get(target, property) {
    if (
      typeof property !== 'string'
      || property in target
      || property.startsWith('__v_')
      || nonMessageAccessorProperties.has(property)
    ) {
      return Reflect.get(target, property);
    }
    return getLazyStringAccessor({ key: property });
  },
}) as LazyStrings;

/* eslint-disable local-rules-named-args/require-named-args -- This type adapter preserves the exact zero-argument or one-object message contract. */
type EnsureString<Message> = Message extends () => infer Result
  ? () => Promise<Awaited<Result>>
  : Message extends (args: infer Args) => infer Result
    ? (args: Args) => Promise<Awaited<Result>>
    : never;
/* eslint-enable local-rules-named-args/require-named-args */

export type EnsureStrings = {
  readonly [Key in keyof Strings]: EnsureString<Strings[Key]>;
};

function getEnsureStringAccessor({ key }: {
  key: string;
}): EnsureStringAccessor {
  const cached = ensureStringAccessorCache.get(key);
  if (cached !== undefined) {
    return cached;
  }

  // This Proxy adapter must preserve both zero-argument and one-object message signatures.
  // eslint-disable-next-line local-rules-named-args/require-named-args
  const accessor = async (...args: readonly unknown[]): Promise<string> => {
    const locale = currentLocaleState.value;
    while (true) {
      await ensureKeyLoaded({ key, locale });
      const message = getActiveMessage({ key, locale });
      if (message !== undefined) {
        return Reflect.apply(message, undefined, args) as string;
      }
      // HMR can replace the owner after the loaded check but before this
      // continuation resumes. Recheck that owner without masking load errors.
    }
  };
  ensureStringAccessorCache.set(key, accessor);
  return accessor;
}

/**
 * Ensures the current-locale boundary is loaded before resolving a message.
 *
 * Use this accessor when a completed string is required immediately, such as
 * for a confirmation, prompt, toast, or another one-shot imperative action.
 */
export const ensureStrings = new Proxy({}, {
  get(target, property) {
    if (
      typeof property !== 'string'
      || property in target
      || property.startsWith('__v_')
      || nonMessageAccessorProperties.has(property)
    ) {
      return Reflect.get(target, property);
    }
    return getEnsureStringAccessor({ key: property });
  },
}) as EnsureStrings;

function hasUnpreparedLocaleBoundaries({ locale }: { locale: UiLocale }): boolean {
  return scheduledBoundaryWarmups.size !== 0
    || [...usedBoundaryIds, ...warmedBoundaryIds].some((boundaryId) => (
      !loadedBoundaryModules[locale].has(boundaryId)
    ));
}

export async function prepareLocale({ locale }: {
  locale: UiLocale;
}): Promise<void> {
  const effectiveLocale = resolveEffectiveLocale({ locale });
  while (true) {
    promoteScheduledBoundaryWarmups();
    const activeBoundaryIds = new Set([
      ...usedBoundaryIds,
      ...warmedBoundaryIds,
    ]);
    await Promise.all([...activeBoundaryIds].map(async (boundaryId) => {
      await ensureBoundaryLoaded({ boundaryId, locale: effectiveLocale });
    }));

    if (!hasUnpreparedLocaleBoundaries({ locale: effectiveLocale })) {
      return;
    }
  }
}

export async function setLocale({ locale }: {
  locale: UiLocale;
}): Promise<void> {
  const effectiveLocale = resolveEffectiveLocale({ locale });
  const request = ++localeSwitchRequest;
  do {
    await prepareLocale({ locale: effectiveLocale });
    if (request !== localeSwitchRequest) {
      return;
    }
    // A module can register or replace a boundary after preparation completes
    // but before this continuation runs. Recheck readiness before committing.
  } while (hasUnpreparedLocaleBoundaries({ locale: effectiveLocale }));

  if (currentLocaleState.value !== effectiveLocale) {
    currentLocaleState.value = effectiveLocale;
    applyDocumentLocale({ locale: effectiveLocale });
  }
}

export const TEST_ONLY = {
  beginBoundaryUpdate,
  finishBoundaryUpdate,
  boundaryIdsByKey,
  boundaryRegistrations,
  ensureBoundaryLoaded,
  async ensureAllRegisteredBoundariesForTest({ locale }: {
    locale: UiLocale;
  }): Promise<void> {
    await Promise.all([...boundaryRegistrations.keys()].map(async (boundaryId) => {
      await ensureBoundaryLoaded({ boundaryId, locale });
    }));
    currentLocaleState.value = locale;
    applyDocumentLocale({ locale });
  },
  loadedBoundaryModules,
  loadingBoundaries,
  registries,
  resolveBrowserLocale,
  resolveEffectiveLocale,
  reset(): void {
    for (const registry of Object.values(registries)) {
      clearRegistry({ registry });
    }
    for (const load of loadingBoundaries.values()) {
      load.cancel();
    }
    boundaryRegistrations.clear();
    boundaryIdsByKey.clear();
    for (const modules of Object.values(loadedBoundaryModules)) {
      modules.clear();
    }
    loadingBoundaries.clear();
    for (const cancel of scheduledBoundaryWarmups.values()) {
      cancel();
    }
    scheduledBoundaryWarmups.clear();
    usedBoundaryIds.clear();
    warmedBoundaryIds.clear();
    localeSwitchRequest = 0;
    boundaryUpdateState = 'idle';
    deferredRegistryKeys.clear();
    currentLocaleState.value = 'en';
    applyDocumentLocale({ locale: 'en' });
  },
  scheduledBoundaryWarmups,
  usedBoundaryIds,
  warmedBoundaryIds,
};
